import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientService, projectService } from 'api';
import type { Project, UpdateProjectDto } from 'api';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { PROJECT_COLORS } from 'shared/constants/colors';
import { queryKeys } from 'shared/constants/queryKeys';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ColorSwatch,
  ColorSwatchRow,
  BillableRateInputRow,
  ColorPickerButton,
  ColorPopoverContent,
  ColorPreviewSwatch,
  RadioFormControl,
  RadioGroupRow,
  RadioOptionControl,
  RadioOptionLabel,
  RadioSectionDescription,
  RadioSectionLabel,
  SettingsContainer,
  SettingsFieldHelperText,
  SettingsFieldLabel,
  SettingsRadioSection,
  SettingsSection,
  SettingsTextField,
  TabCardShell,
} from '../projects.styles';

import { ClientSelect } from './ClientSelect';

interface Props {
  projectId: string;
  project: Project;
}

const ProjectSettingsTab: React.FC<Props> = ({ projectId, project }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { canAssignProjectMembers } = usePermissions();

  const [name, setName] = useState(project.name);
  const [nameError, setNameError] = useState('');
  const [clientId, setClientId] = useState(project.clientId ?? '');
  const [color, setColor] = useState(project.color ?? PROJECT_COLORS[0]);
  const [isBillable, setIsBillable] = useState(project.isBillable);
  const [billableRate, setBillableRate] = useState(
    project.billableRate != null ? String(project.billableRate) : '',
  );
  const [estimatedHours, setEstimatedHours] = useState(
    project.estimatedHours != null ? String(project.estimatedHours) : '',
  );
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(project.name);
    setClientId(project.clientId ?? '');
    setColor(project.color ?? PROJECT_COLORS[0]);
    setIsBillable(project.isBillable);
    setBillableRate(project.billableRate != null ? String(project.billableRate) : '');
    setEstimatedHours(project.estimatedHours != null ? String(project.estimatedHours) : '');
  }, [project]);

  const { data: clients = [] } = useQuery({
    queryKey: queryKeys.clients.list(),
    queryFn: () => clientService.getClients().then((res) => res.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProjectDto) => projectService.updateProject(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      enqueueSnackbar('Project updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update project', { variant: 'error' });
    },
  });

  const buildPayload = (overrides: Partial<UpdateProjectDto>): UpdateProjectDto => ({
    name,
    color,
    clientId: clientId || undefined,
    isPublic: project.isPublic,
    isBillable,
    status: project.status,
    billableRate: billableRate !== '' ? Number(billableRate) : undefined,
    estimatedHours: estimatedHours !== '' ? Number(estimatedHours) : undefined,
    ...overrides,
  });

  const save = (overrides: Partial<UpdateProjectDto>) =>
    updateMutation.mutate(buildPayload(overrides));

  return (
    <TabCardShell variant="outlined">
      <SettingsContainer>
        <SettingsSection>
          <SettingsFieldLabel>Name</SettingsFieldLabel>
          <SettingsTextField
            inputRef={nameRef}
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError('');
            }}
            onBlur={() => {
              if (!name.trim()) {
                setNameError('Project name is required.');
                return;
              }
              if (name !== project.name) {
                save({ name: name.trim() });
              }
            }}
            error={Boolean(nameError)}
            size="small"
          />
          {nameError && <FormHelperText error>{nameError}</FormHelperText>}
        </SettingsSection>

        <Divider />

        <SettingsSection>
          <SettingsFieldLabel>Client</SettingsFieldLabel>
          <div style={{ width: '100%', maxWidth: 384 }}>
            <ClientSelect
              clients={clients}
              value={clientId}
              onChange={(id) => {
                setClientId(id);
                save({ clientId: id || undefined });
              }}
            />
          </div>
          <SettingsFieldHelperText>
            Used for grouping similar projects together.
          </SettingsFieldHelperText>
        </SettingsSection>

        <Divider />

        <SettingsSection>
          <SettingsFieldLabel>Color</SettingsFieldLabel>
          <ColorPickerButton
            variant="outlined"
            size="small"
            onClick={(e) => setColorAnchorEl(e.currentTarget)}
          >
            <ColorPreviewSwatch sx={{ backgroundColor: color }} />▾
          </ColorPickerButton>
          <Popover
            open={Boolean(colorAnchorEl)}
            anchorEl={colorAnchorEl}
            onClose={() => setColorAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <ColorPopoverContent>
              <ColorSwatchRow>
                {PROJECT_COLORS.map((c) => (
                  <Tooltip key={c} title={c}>
                    <ColorSwatch
                      swatchcolor={c}
                      selected={color === c}
                      onClick={() => {
                        setColor(c);
                        setColorAnchorEl(null);
                        save({ color: c });
                      }}
                    />
                  </Tooltip>
                ))}
              </ColorSwatchRow>
            </ColorPopoverContent>
          </Popover>
          <SettingsFieldHelperText>
            Use color to visually differentiate projects.
          </SettingsFieldHelperText>
        </SettingsSection>

        <Divider />

        <SettingsRadioSection>
          <RadioSectionLabel>Visibility</RadioSectionLabel>
          <RadioSectionDescription>
            Only people you add to the Project can track time on it.
          </RadioSectionDescription>
          <RadioFormControl>
            <RadioGroupRow
              row
              value={project.isPublic ? 'public' : 'private'}
              onChange={(e) => save({ isPublic: e.target.value === 'public' })}
            >
              <RadioOptionLabel
                value="private"
                control={<RadioOptionControl color="primary" size="small" />}
                label={<Typography variant="body2">Private</Typography>}
                disabled={!canAssignProjectMembers}
              />
              <RadioOptionLabel
                value="public"
                control={<RadioOptionControl color="primary" size="small" />}
                label={<Typography variant="body2">Public</Typography>}
                disabled={!canAssignProjectMembers}
              />
            </RadioGroupRow>
          </RadioFormControl>
        </SettingsRadioSection>

        <Divider />

        <SettingsRadioSection>
          <RadioSectionLabel>Billability</RadioSectionLabel>
          <RadioSectionDescription>
            All new entries on this project will initially be set as billable.
          </RadioSectionDescription>
          <RadioFormControl>
            <RadioGroupRow
              row
              value={isBillable ? 'billable' : 'nonBillable'}
              onChange={(e) => {
                const nextIsBillable = e.target.value === 'billable';
                setIsBillable(nextIsBillable);
                save({ isBillable: nextIsBillable });
              }}
            >
              <RadioOptionLabel
                value="billable"
                control={<RadioOptionControl color="primary" size="small" />}
                label={<Typography variant="body2">Billable</Typography>}
              />
              <RadioOptionLabel
                value="nonBillable"
                control={<RadioOptionControl color="primary" size="small" />}
                label={<Typography variant="body2">Non-billable</Typography>}
              />
            </RadioGroupRow>
          </RadioFormControl>
        </SettingsRadioSection>

        <Divider />

        <SettingsSection>
          <SettingsFieldLabel>Project billable rate</SettingsFieldLabel>
          <BillableRateInputRow>
            <SettingsTextField
              size="small"
              type="number"
              value={billableRate}
              onChange={(e) => setBillableRate(e.target.value)}
              placeholder="0.00"
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: 160 }}
            />
            <Typography variant="caption" color="text.secondary">
              /hr
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              sx={{ borderRadius: '10px' }}
              onClick={() =>
                save({ billableRate: billableRate !== '' ? Number(billableRate) : undefined })
              }
              disabled={updateMutation.isPending || !billableRate}
            >
              Set rate
            </Button>
          </BillableRateInputRow>
          <SettingsFieldHelperText>
            Billable rate used for calculating billable amount for this project.
          </SettingsFieldHelperText>
        </SettingsSection>

        <Divider />

        <SettingsSection>
          <SettingsFieldLabel>Project estimate</SettingsFieldLabel>
          <SettingsTextField
            size="small"
            type="number"
            value={estimatedHours}
            onChange={(e) => setEstimatedHours(e.target.value)}
            onBlur={() =>
              save({
                estimatedHours: estimatedHours !== '' ? Number(estimatedHours) : undefined,
              })
            }
            placeholder="0"
            inputProps={{ min: 0, step: 0.5 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
            }}
            sx={{ width: 200 }}
          />
          <SettingsFieldHelperText>
            Choose how you wish to track project progress.
          </SettingsFieldHelperText>
        </SettingsSection>
      </SettingsContainer>
    </TabCardShell>
  );
};

export default ProjectSettingsTab;
